import { FC, JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QESButton } from "@app/shared/qes-standards/Button";
import { NetworkSubmissionContext } from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import { ExceptionGapRow, ExceptionGroupRow, NetworkSubmissionSummary } from "@app/compliance-connect/network-submission/interfaces";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import NetworkSubmissionExceptionFlyout from "@app/compliance-connect/network-submission/NetworkSubmissionExceptionFlyout";
import { ProjectPerspective } from "@app/projects/enumerators";
import { getProjectPerspectiveDescription } from "@app/projects/projectPerspectiveConversions";
import { useQuery } from "@tanstack/react-query";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";


const NO_RESULTS_ROW: JSX.Element = (
  <div className="ns-exceptions__gray-background ns-exceptions__border-bottom ps-5 py-3 text-muted">
    No results found.
  </div>
);

const SKELETON_BONE = (
  <div className="skeleton shimmer">
    <div className="bone" style={{  borderRadius: 2, height: "1rem" }} />
  </div>
);

const perspectiveItems: DropdownItem<ProjectPerspective>[] =
  [ProjectPerspective.ByCounty, ProjectPerspective.BySpecialty].map((p) => ({
    key: p,
    name: getProjectPerspectiveDescription(p),
  }));

const toggleIcon = (expanded: boolean): JSX.Element => (
  <QesIcon className="ms-4 me-2"
    iconName={expanded ? QesIcons.ChevronUp : QesIcons.ChevronDown}
    description={expanded ? "Collapse" : "Expand"} />
);


interface NetworkSubmissionStepExceptionsProps {
  setSummary: (summary: NetworkSubmissionSummary) => void;
}

export const NetworkSubmissionStepExceptions: FC<NetworkSubmissionStepExceptionsProps> = ({ setSummary }) => {
  const wizardContext = useContext(NetworkSubmissionContext);
  const [countyFilter, setCountyFilter] = useState<DropdownItem<string>[] | null>(null);
  const [specialtyFilter, setSpecialtyFilter] = useState<DropdownItem<string>[] | null>(null);
  const [perspective, setPerspective] = useState(ProjectPerspective.ByCounty);
  const [expandedGroupsWith, setExpandedGroupsWith] = useState<Set<string>>(new Set());
  const [expandedGroupsWithout, setExpandedGroupsWithout] = useState<Set<string>>(new Set());
  const [expandedWith, setExpandedWith] = useState<boolean>(false);
  const [expandedWithout, setExpandedWithout] = useState<boolean>(false);

  const networkSubmissionService = useRef(new NetworkSubmissionService()).current;

  const countyOptionsRef = useRef<DropdownItem<string>[]>([]);
  const specialtyOptionsRef = useRef<DropdownItem<string>[]>([]);

  const countyIds = useMemo(() => {
    if (countyFilter === null) return [];
    const total = countyOptionsRef.current.length;
    if (total > 0 && countyFilter.length === total) return [];
    return countyFilter.map(c => c.key);
  }, [countyFilter]);

  const specialtyIds = useMemo(() => {
    if (specialtyFilter === null) return [];
    const total = specialtyOptionsRef.current.length;
    if (total > 0 && specialtyFilter.length === total) return [];
    return specialtyFilter.map(s => s.key);
  }, [specialtyFilter]);

  const debouncedCountyIds = useDebouncedValue(countyIds, 1000);
  const debouncedSpecialtyIds = useDebouncedValue(specialtyIds, 1000);

  const { data: reviewData, isLoading } = useQuery({
    enabled: !!wizardContext.selectedProject,
    queryFn: ({ signal }) => networkSubmissionService.getExceptionReview(wizardContext.selectedProject!.id, debouncedCountyIds, debouncedSpecialtyIds, signal),
    queryKey: ["ns-exception-review", wizardContext.selectedProject?.id, JSON.stringify(debouncedCountyIds), JSON.stringify(debouncedSpecialtyIds)],
  });

  const pendingExceptions = wizardContext.pendingExceptions;

  const mergedExceptionGaps = useMemo<ExceptionGapRow[]>(() => {
    if (!reviewData) return [];
    return reviewData.gaps.map(gap => ({
      ...gap,
      hasException: gap.hasException || pendingExceptions.has(`${gap.countyId}:${gap.specialtyGroupCode}`),
    }));
  }, [reviewData, pendingExceptions]);

  useEffect(() => {
    if (reviewData) {
      setSummary({
        missingExceptions: mergedExceptionGaps.filter(g => !g.hasException).length,
        totalGaps: mergedExceptionGaps.length,
      });
    }
  }, [mergedExceptionGaps, reviewData, setSummary]);

  const countyDropdownItems = useMemo<DropdownItem<string>[]>(() => {
    if (reviewData && debouncedCountyIds.length === 0 && debouncedSpecialtyIds.length === 0) {
      const uniqueCounties = new Map<string, string>();
      reviewData.gaps.forEach(r => uniqueCounties.set(r.countyId, r.countyName));
      countyOptionsRef.current = Array.from(uniqueCounties, ([key, name]) => ({ key, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return countyOptionsRef.current;
  }, [reviewData, debouncedCountyIds, debouncedSpecialtyIds]);

  const specialtyDropdownItems = useMemo<DropdownItem<string>[]>(() => {
    if (reviewData && debouncedCountyIds.length === 0 && debouncedSpecialtyIds.length === 0) {
      const uniqueSpecialties = new Map<string, string>();
      reviewData.gaps.forEach(r => uniqueSpecialties.set(r.specialtyGroupCode, r.specialtyGroupDisplayName));
      specialtyOptionsRef.current = Array.from(uniqueSpecialties, ([key, name]) => ({ key, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return specialtyOptionsRef.current;
  }, [reviewData, debouncedCountyIds, debouncedSpecialtyIds]);

  const buildGroupRows = useCallback((withExceptions: boolean): ExceptionGroupRow[] => {
    const groupMap = new Map<string, { name: string; rows: ExceptionGapRow[] }>();

    mergedExceptionGaps.forEach(row => {
      const id = perspective === ProjectPerspective.ByCounty ? row.countyId : row.specialtyGroupCode;
      const name = perspective === ProjectPerspective.ByCounty ? row.countyName : row.specialtyGroupDisplayName;
      const entry = groupMap.get(id);

      if (entry) {
        entry.rows.push(row);
      } else {
        groupMap.set(id, { name, rows: [row] });
      }
    });

    return Array.from(groupMap.entries())
      .filter(([, { rows }]) => rows.some(r => r.hasException === withExceptions))
      .map(([id, { name, rows }]) => ({
        exceptionDeficit: rows.filter(r => !r.hasException).length,
        fillable: rows.filter(r => r.fillable).length,
        gaps: rows.length,
        id,
        name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [mergedExceptionGaps, perspective]);

  const filteredWithout = useMemo<ExceptionGroupRow[]>(() => buildGroupRows(false), [buildGroupRows]);
  const filteredWith = useMemo<ExceptionGroupRow[]>(() => buildGroupRows(true), [buildGroupRows]);

  const getChildRows = useCallback((groupId: string, withExceptions: boolean): ExceptionGapRow[] => {
    return mergedExceptionGaps.filter(row =>
      row.hasException === withExceptions
      && (perspective === ProjectPerspective.ByCounty ? row.countyId === groupId : row.specialtyGroupCode === groupId)
    );
  }, [mergedExceptionGaps, perspective]);

  const toggleGroup = (withExceptions: boolean, id: string): void => {
    const setExpanded = withExceptions ? setExpandedGroupsWith : setExpandedGroupsWithout;
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleClear = (): void => {
    setCountyFilter(null);
    setSpecialtyFilter(null);
  };

  const renderSkeletonRows = (count: number): JSX.Element[] =>
    Array.from({ length: count }, (_, i) => (
      <div key={`skeleton-${i}`} className="d-flex">
        <div className="ns-exceptions__flex-30 py-3 pe-3">{SKELETON_BONE}</div>
        <div className="ns-exceptions__flex-21 py-3 pe-3">{SKELETON_BONE}</div>
        <div className="ns-exceptions__flex-19 py-3 pe-3">{SKELETON_BONE}</div>
        <div className="ns-exceptions__flex-30 py-3 pe-3">{SKELETON_BONE}</div>
      </div>
    ));

  const renderGapDetailRows = (groupId: string, withExceptions: boolean): JSX.Element => {
    const rows = getChildRows(groupId, withExceptions);

    return (
      <div className="ns-exceptions__gray-background">
        <div className="ns-exceptions__gap-detail-header ns-exceptions__border-bottom py-3 mx-5">
          <div className="d-flex align-items-center py-2">
            <div className="ns-exceptions__flex-30" />
            <div className="ns-exceptions__gap-col-spr ns-exceptions__flex-70">
              <strong>Servicing Provider Requirements</strong>
            </div>
          </div>
          <div className="d-flex align-items-center py-2 ns-exceptions__gap-col-headers">
            <div className="ns-exceptions__flex-30" />
            <div className="ns-exceptions__flex-7"><strong>Required</strong></div>
            <div className="ns-exceptions__flex-7"><strong>In County</strong></div>
            <div className="ns-exceptions__flex-7"><strong>Servicing</strong></div>
            <div className="ns-exceptions__flex-6"><strong>Met</strong></div>
            <div className="ns-exceptions__flex-6"><strong>Fillable</strong></div>
            <div className="ns-exceptions__flex-7"><strong>Gap Age</strong></div>
            <div className="ns-exceptions__flex-30">
              {!withExceptions && <strong>Exception Request Form</strong>}
            </div>
          </div>
        </div>
        <div className="ns-exceptions__gap-detail-rows ns-exceptions__border-bottom py-2 mx-5">
          {rows.length === 0 ? NO_RESULTS_ROW : (
            rows.map((row, idx) => (
              <div key={perspective === ProjectPerspective.ByCounty ? row.specialtyGroupCode : row.countyId}
                className="d-flex align-items-center py-2">
                <div className="ns-exceptions__flex-30">
                  <div className="ps-5">
                    <span>{perspective === ProjectPerspective.ByCounty ? row.specialtyGroupDisplayName : row.countyName}</span>
                  </div>
                </div>
                <div className="ns-exceptions__flex-7">{row.required}</div>
                <div className="ns-exceptions__flex-7">{row.inCounty}</div>
                <div className="ns-exceptions__flex-7">{row.servicing}</div>
                <div className="ns-exceptions__flex-6">{row.met ? "Y" : "N"}</div>
                <div className="ns-exceptions__flex-6">{row.fillable ? "Y" : "N"}</div>
                <div className="ns-exceptions__flex-7">{row.gapAge} days</div>
                <div className="ns-exceptions__flex-30">
                  {!withExceptions && (
                    <FlyoutLink
                      classNames="qes-button-secondary btn-link p-0"
                      content={{
                        body: (
                          <NetworkSubmissionExceptionFlyout
                            row={row}
                          />
                        ),
                        title: "Specialty Exception Information",
                      }}
                      size="40"
                      testId={`ns-exceptions__enter-exception-${idx}`}
                    >
                      Enter Exception
                    </FlyoutLink>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderGroupRows = (groups: ExceptionGroupRow[], withExceptions: boolean): JSX.Element => {
    const expandedGroups = withExceptions ? expandedGroupsWith : expandedGroupsWithout;
    return (
      <>
        {groups.length === 0 ? NO_RESULTS_ROW :
          groups.map(group => (
            <div key={group.id}>
              <div
                className="cursor-pointer py-3 ns-exceptions__section-row ns-exceptions__border-bottom"
                onClick={() => toggleGroup(withExceptions, group.id)}
                data-testid={`ns-exceptions__group-row--${group.id}`}
              >
                <div className="d-flex align-items-center mx-5">
                  <div className="ns-exceptions__flex-30">
                    {toggleIcon(expandedGroups.has(group.id))}
                    <span>{group.name}</span>
                  </div>
                  <div className="ns-exceptions__flex-21">{group.gaps}</div>
                  <div className="ns-exceptions__flex-19">{group.fillable}</div>
                  <div className="ns-exceptions__flex-30">{group.exceptionDeficit}</div>
                </div>
              </div>
              {expandedGroups.has(group.id) && renderGapDetailRows(group.id, withExceptions)}
            </div>
          ))
        }
      </>
    );
  };

  return (
    <>
      <fieldset className="qes-searchbox bg-white d-flex align-items-end gap-4 mt-4 p-4">
        <MultiselectDropdown<string>
          containerClassName="ns-exceptions__county-dropdown w-25 me-3"
          items={countyDropdownItems}
          label="County"
          onSelectionChanged={setCountyFilter}
          selectedItems={countyFilter ?? countyDropdownItems}
          testId="ns-exceptions__county-filter"
        />
        <MultiselectDropdown<string>
          containerClassName="ns-exceptions__specialty-dropdown w-25 me-3"
          items={specialtyDropdownItems}
          label="Specialty"
          onSelectionChanged={setSpecialtyFilter}
          selectedItems={specialtyFilter ?? specialtyDropdownItems}
          testId="ns-exceptions__specialty-filter"
        />
        <QESButton
          qesType="secondary"
          qesTestId="ns-exceptions__clear-btn"
          className="btn-link"
          onClick={handleClear}
        >
          Clear All
        </QESButton>
      </fieldset>

      <NewActionMenu
        buttonText={`View by: ${perspectiveItems.find(p => p.key === perspective)?.name}`}
        testId="ns-exceptions__view-by-dropdown"
        buttonClassNames="qes-button-secondary"
        closeOnClick={true}
        loading={isLoading}
        containerClassnames="m-2"
      >
        {perspectiveItems.map(item => (
          <NewActionMenuItem
            display={item.name}
            isSelected={item.key === perspective}
            key={item.key}
            onClick={() => setPerspective(item.key)}
            testId={`ns-exceptions__view-by-option--${item.key}`}
          />
        ))}
      </NewActionMenu>

      <div className="w-100" data-testid="ns-exceptions__table">
        <div className="ns-exceptions__table-header-row ns-exceptions__border-bottom py-3">
          <div className="d-flex align-items-center mx-5">
            <div className="ns-exceptions__flex-30" />
            <div className="ns-exceptions__flex-21"><strong>Gaps</strong></div>
            <div className="ns-exceptions__flex-19"><strong>Fillable</strong></div>
            <div className="ns-exceptions__flex-30"><strong>Exception Deficit</strong></div>
          </div>
        </div>
        <div>
          {isLoading ? renderSkeletonRows(5)
            : (<>
              <div
                className="cursor-pointer py-3 ns-exceptions__section-row ns-exceptions__border-bottom"
                onClick={() => setExpandedWithout(prev => !prev)}
                data-testid="ns-exceptions__section-without"
              >
                {toggleIcon(expandedWithout)}
                <strong>Gaps without Exceptions Filed</strong>
              </div>
              {expandedWithout && renderGroupRows(filteredWithout, false)}

              <div
                className="cursor-pointer py-3 ns-exceptions__section-row ns-exceptions__border-bottom"
                onClick={() => setExpandedWith(prev => !prev)}
                data-testid="ns-exceptions__section-with"
              >
                {toggleIcon(expandedWith)}
                <strong>Gaps with Exceptions</strong>
              </div>
              {expandedWith && renderGroupRows(filteredWith, true)}
            </>)
          }
        </div>
      </div>
    </>
  );
};
